import {
    TranscriptionDestinationType,
    useRemainingQuota,
} from 'flow/hooks/api/useTranscriptionBilling';
import useAppContext from 'flow/hooks/useAppContext';
import { ExtensionKey } from 'flow/types/EntityKeys';

import QuotaExceededNotice from './QuotaExceededNotice';
import QuotaLimitRow from './QuotaLimitRow';
import QuotaPercentRow from './QuotaPercentRow';
import QuotaProgressBar from './QuotaProgressBar';
import QuotaUsageRow from './QuotaUsageRow';

type Props = {
    extensionKey: ExtensionKey;
    destination: TranscriptionDestinationType;
};

const QuotaSection = ({ extensionKey, destination }: Props) => {
    const { i18next } = useAppContext();
    const { data, isPending, error } = useRemainingQuota(
        extensionKey,
        destination,
    );

    if (!isPending && (error || !data)) {
        return (
            <p className="text-sm text-gray-600">
                {i18next.t('settings.transcriptions.quota.error')}
            </p>
        );
    }

    const totalMinutes = data?.totalMinutes ?? null;
    const usedMinutes = data?.usedMinutes ?? null;
    const remainingMinutes = data?.remainingMinutes ?? null;
    const usagePercent =
        totalMinutes !== null && usedMinutes !== null && totalMinutes > 0
            ? Math.round((usedMinutes / totalMinutes) * 100)
            : null;
    const quotaIsExceeded = remainingMinutes !== null && remainingMinutes <= 0;

    return (
        <>
            <QuotaProgressBar percent={usagePercent} />

            <div className="flex flex-col gap-y-2 py-2">
                <QuotaLimitRow total={totalMinutes} />
                <QuotaUsageRow minutes={usedMinutes} />
                <QuotaPercentRow percent={usagePercent} />
                {quotaIsExceeded && <QuotaExceededNotice />}
            </div>
        </>
    );
};

export default QuotaSection;
